import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { ChartBar, CaretDown, CaretUp, MapPin, Buildings, WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";

// Per-organization coverage dashboard: auctions found per region / province
// (within the monitored scope), active sources, last scan, errors per source.
export default function RadarCoverage({ version }) {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/radar/coverage");
            setData(data);
        } catch { /* ignore */ } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (open) load(); }, [open, load, version]);

    const fmtDate = (iso) => {
        if (!iso) return "—";
        try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); }
        catch { return iso; }
    };

    return (
        <div className="border border-gray-200 bg-white mb-6" data-testid="radar-coverage">
            <button
                onClick={() => setOpen((o) => !o)}
                data-testid="radar-coverage-toggle"
                className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <ChartBar size={18} weight="fill" />
                    <h2 className="text-base font-bold">Copertura · aste trovate per area</h2>
                </div>
                {open ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
            </button>

            {open && (
                <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-5" data-testid="radar-coverage-body">
                    {loading && <p className="text-sm text-gray-400">Caricamento…</p>}

                    {data && (
                        <>
                            {/* Meta row */}
                            <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs">
                                <div data-testid="coverage-total">
                                    <span className="text-gray-500">Totale aste monitorate: </span>
                                    <span className="font-bold">{data.total}</span>
                                </div>
                                <div data-testid="coverage-last-scan">
                                    <span className="text-gray-500">Ultima scansione: </span>
                                    <span className="font-semibold">{fmtDate(data.last_scan_at)}</span>
                                    {data.crawl_in_progress && (
                                        <span className="ml-2 inline-flex items-center gap-1 text-emerald-700">
                                            <ArrowsClockwise size={12} weight="bold" className="animate-spin" /> in corso
                                        </span>
                                    )}
                                </div>
                                <div data-testid="coverage-sources">
                                    <span className="text-gray-500">Fonti attive: </span>
                                    <span className="font-semibold">
                                        {(data.active_sources || []).join(", ") || "—"}
                                    </span>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-5">
                                {/* By region */}
                                <div data-testid="coverage-by-region">
                                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                                        <MapPin size={13} weight="fill" /> Per regione
                                    </div>
                                    {data.by_region?.length ? (
                                        <ul className="space-y-1">
                                            {data.by_region.map((r) => (
                                                <li key={r.region} className="flex justify-between text-sm border-b border-gray-50 py-1">
                                                    <span>{r.region}</span>
                                                    <span className="font-semibold tabular-nums">{r.count}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : <p className="text-sm text-gray-400">Nessuna asta ancora.</p>}
                                </div>

                                {/* By province */}
                                <div data-testid="coverage-by-province">
                                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                                        <Buildings size={13} weight="fill" /> Per provincia
                                    </div>
                                    {data.by_province?.length ? (
                                        <ul className="space-y-1 max-h-56 overflow-y-auto pr-1">
                                            {data.by_province.map((p) => (
                                                <li key={p.province} className="flex justify-between text-sm border-b border-gray-50 py-1">
                                                    <span>{p.province}</span>
                                                    <span className="font-semibold tabular-nums">{p.count}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : <p className="text-sm text-gray-400">Nessuna asta ancora.</p>}
                                </div>
                            </div>

                            {/* Errors per source */}
                            {data.errors_by_source?.length > 0 && (
                                <div data-testid="coverage-errors">
                                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-600 mb-2">
                                        <WarningCircle size={13} weight="fill" /> Errori per fonte (24h)
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {data.errors_by_source.map((e) => (
                                            <span key={e.source} className="inline-flex items-center gap-1.5 text-xs border border-amber-200 bg-amber-50 text-amber-800 px-2.5 py-1 rounded">
                                                {e.source}: <strong>{e.errors}</strong>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
